<template>
  <NdsTooltip
    class="ndsMenuItemTooltip"
    :content="disabledTooltip"
    :enabled="disabled && !!disabledTooltip"
    :position="NdsTooltipPosition.TOP_START"
  >
    <div
      class="ndsMenuItem"
      :class="[
        { '-selected': value && !isCheckable },
        { '-checked': value && isCheckable },
        { '-disabled': disabled },
        { '-hasBorder': hasBorder },
      ]"
      data-testid="nds-menu-item"
      role="menuitem"
      :style="{ '--level': level }"
      :tabindex="disabled ? -1 : 0"
      @click="handleMenuItemClick"
      @keydown.enter="handleMenuItemClick"
      @keydown.space="handleMenuItemClick"
      @keydown.tab.stop
    >
      <slot name="content">
        <div class="ndsMenuItem__checkbox">
          <NdsInputCheckbox
            v-if="isCheckable"
            v-model="value"
            class="ndsMenuItem__checkbox"
            data-testid="nds-menu-item-checkbox"
            :is-focusable="false"
            option="option"
            :size="NdsInputCheckboxSize.S"
            @click.prevent="handleMenuItemClick"
          />
        </div>

        <div class="ndsMenuItem__content">
          <slot
            v-if="hasIconLeft"
            name="iconLeft"
          >
            <NdsIcon
              class="ndsMenuItem__iconLeft"
              data-testid="nds-menu-item-icon-left"
              :icon="iconLeft"
              :size="NdsIconSize.S"
            />
          </slot>

          <NdsTag
            v-if="tag"
            class="ndsMenuItem__tag"
            :color="tagColor"
            data-testid="nds-menu-item-tag"
            :label="tag"
            :size="NdsTagSize.COMPACT"
          />

          <slot name="label">
            <NdsLineClamp
              v-if="label"
              ref="labelLineClamp"
              class="ndsMenuItem__label"
              data-testid="nds-menu-item-label"
              :max-lines="1"
              :tooltip="label"
            >
              <NdsText :size="NdsTextSizes.M">
                {{ label }}
              </NdsText>
            </NdsLineClamp>
          </slot>

          <NdsBadge
            v-if="hasBadge"
            class="ndsMenuItem__badge"
            :color="badgeColor"
          />
        </div>
      </slot>

      <slot
        v-if="hasIconRight"
        name="iconRight"
      >
        <NdsIcon
          class="ndsMenuItem__iconRight"
          data-testid="nds-menu-item-icon-right"
          :icon="iconRight"
        />
      </slot>
    </div>
  </NdsTooltip>
</template>

<script lang="ts" setup>
import { ref, type PropType } from 'vue'
import { NdsBadgeColor } from '@/components/NewDesignSystem/NdsBadge/NdsBadge.interfaces'
import NdsBadge from '@/components/NewDesignSystem/NdsBadge/NdsBadge.vue'
import { NdsIconSize, NdsIconType } from '@/components/NewDesignSystem/NdsIcon/NdsIcon.interfaces'
import NdsIcon from '@/components/NewDesignSystem/NdsIcon/NdsIcon.vue'
import { NdsInputCheckboxSize } from '@/components/NewDesignSystem/NdsInputCheckbox/NdsInputCheckbox.interfaces'
import NdsInputCheckbox from '@/components/NewDesignSystem/NdsInputCheckbox/NdsInputCheckbox.vue'
import { NdsTagColor, NdsTagSize } from '@/components/NewDesignSystem/NdsTag/NdsTag.interfaces'
import NdsTag from '@/components/NewDesignSystem/NdsTag/NdsTag.vue'
import { NdsTextSizes } from '@/components/NewDesignSystem/NdsText/NdsText.interfaces'
import NdsText from '@/components/NewDesignSystem/NdsText/NdsText.vue'
import NdsTooltip from '@/components/NewDesignSystem/NdsTooltip/NdsTooltip.vue'
import NdsLineClamp from '@/components/NewDesignSystem/NdsLineClamp/NdsLineClamp.vue'
import { NdsTooltipPosition } from '@/components/NewDesignSystem/NdsTooltip/NdsTooltip.interfaces'

const labelLineClamp = ref<InstanceType<typeof NdsLineClamp> | null>(null)

const emit = defineEmits(['input'])

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  isCheckable: {
    type: Boolean,
    default: true,
  },
  level: {
    type: Number,
    default: 0,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  disabledTooltip: {
    type: String,
  },
  hasIconLeft: {
    type: Boolean,
    default: false,
  },
  iconLeft: {
    type: String as PropType<NdsIconType>,
    default: NdsIconType.PLUS,
  },
  hasIconRight: {
    type: Boolean,
    default: false,
  },
  hasBorder: {
    type: Boolean,
    default: false,
  },
  iconRight: {
    type: String as PropType<NdsIconType>,
    default: NdsIconType.CHEVRON_RIGHT,
  },
  label: {
    type: String,
  },
  tag: {
    type: String,
  },
  tagColor: {
    type: String as PropType<NdsTagColor>,
    default: NdsTagColor.NEUTRAL,
  },
  hasBadge: {
    type: Boolean,
    default: false,
  },
  badgeColor: {
    type: String as PropType<NdsBadgeColor>,
    default: NdsBadgeColor.PRIMARY,
  },
})

const handleMenuItemClick = () => {
  if (!props.disabled) {
    emit('input', !props.value)
  }
}
</script>

<style lang="scss" scoped>
.ndsMenuItemTooltip {
  width: 100%;
}

.ndsMenuItem {
  padding: var(--spacing-1-5) var(--spacing-1) var(--spacing-1-5)
    calc(var(--spacing-1) + (var(--level) * var(--spacing-1)));
  background-color: white;

  display: flex;
  justify-content: space-between;
  align-items: center;

  border-radius: var(--spacing-1);

  width: 100%;
  height: 40px;

  cursor: pointer;

  &__content {
    display: flex;
    flex: 1;
    width: 100%;
    overflow: hidden;

    gap: var(--spacing-1);

    align-items: center;
  }

  &__tag {
    flex: 0 0 auto;
    width: fit-content;
    max-width: 30%;
  }

  &__label {
    flex: 0 1 auto;
    min-width: 0;
  }

  &.-selected {
    background: var(--primary-50);

    &:hover {
      background: var(--primary-100);
    }

    &:active {
      background: var(--primary-200);
    }
  }

  &:hover {
    background: var(--neutral-100);
  }

  &:active {
    background: var(--neutral-200);
  }

  &.-disabled {
    cursor: not-allowed;
    user-select: none;

    opacity: 0.6;

    &:hover {
      background: white;
    }
  }

  &.-hasBorder {
    border: 1px solid var(--neutral-300);
  }
}
</style>
